import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { fractionFromPointer, railPixelWidth } from "./railSplit";

const HANDLE_HALF = 7; // the drag handle is 14px wide, centred on the gap

/** Drag handlers for the divider; spread onto the handle element. */
export type SplitHandleProps = {
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
};

/**
 * A draggable two-pane split where the TRAILING (right) pane takes a fraction of
 * the container. Shared by Home (document rail 60%) and the draft workspace
 * (chat 40%) so both behave identically: the width is a FRACTION of the measured
 * container (never saved as pixels), neither pane drops under its minimum, pointer
 * capture keeps the drag alive over iframes / outside the window, and a
 * double-click on the handle resets to the default.
 *
 * `enabled = false` (e.g. a stacked mobile layout or a collapsed pane) turns the
 * hook inert: `sideWidth` is undefined and the caller keeps its own layout.
 */
export function useSplitDrag({
  containerRef,
  defaultFraction,
  enabled = true,
}: {
  containerRef: RefObject<HTMLElement | null>;
  defaultFraction: number;
  enabled?: boolean;
}): { sideWidth: number | undefined; gap: number; handleProps: SplitHandleProps } {
  const [fraction, setFraction] = useState(defaultFraction);
  const [box, setBox] = useState<{ width: number; gap: number }>({ width: 0, gap: 16 });
  const draggingRef = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (el === null) return;
    const measure = (): void => {
      const gap = Number.parseFloat(getComputedStyle(el).columnGap);
      const next = { width: el.clientWidth, gap: Number.isFinite(gap) ? gap : 16 };
      // Keep the same object when nothing changed, so a measure never re-renders.
      setBox((prev) => (prev.width === next.width && prev.gap === next.gap ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, enabled]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* no capture available: the drag still works while over the handle */
    }
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      const el = containerRef.current;
      if (el === null) return;
      const rect = el.getBoundingClientRect();
      setFraction(
        fractionFromPointer(e.clientX, rect.right, rect.width, box.gap, HANDLE_HALF)
      );
    },
    [containerRef, box.gap]
  );

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* already released */
    }
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  const sideWidth = enabled ? railPixelWidth(fraction, box.width, box.gap) : undefined;
  return {
    sideWidth,
    gap: box.gap,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onDoubleClick: () => setFraction(defaultFraction),
    },
  };
}
